import { useState } from "react";
import { Modal, Button, Divider, Input } from "antd";
import { PlusOutlined, EyeOutlined, DeleteOutlined, EditOutlined, SearchOutlined } from "@ant-design/icons";
import styled from "styled-components";
import axios from "utils/axios";
import configFile from "config.json";
import { NotificationManager } from "react-notifications";

const API_BASE = `${configFile.PROD_SERVER_URL}/menu2`;

const ImagesTab = ({ image, availableImages = [], onImageSelect, menuId, instance }) => {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [search, setSearch] = useState("");

    const filteredImages = availableImages.filter((img) =>
        (img.name || "").toLowerCase().includes(search.trim().toLowerCase())
    );

    const handleSaveImages = async () => {
        if (!instance?.id) {
            NotificationManager.error("Бүтээгдэхүүн сонгоно уу.");
            return;
        }
        try {
            let parsedImgUrl = image?.url ? new URL(image.url) : null
            const payload = {
                name: instance.name,
                majorGroupId: instance.majorGroupId,
                defaultPrice: instance.defaultPrice,
                description: instance.description,
                type: instance.type,
                imageUrl: parsedImgUrl
            };
            if (instance?.isDraft) {
                await axios.post(`${API_BASE}/manageMenu/createMenu`, payload);
            } else {
                await axios.post(`${API_BASE}/manageMenu/updateMenu`, {
                    ...payload,
                    id: instance.id,
                });
            }
            NotificationManager.success("Амжилттай");
        } catch (err) {
            NotificationManager.error(
                err?.response?.data?.errorMessage || "Амжилтгүй",
                "Error",
                5000
            );
        }
    };

    const closePickerModal = () => {
        setPickerOpen(false);
        setSearch("");
    };

    return (
        <div>
            <SectionTitle>Бүтээгдэхүүний зураг</SectionTitle>

            {image?.url ? (
                <SelectedImageWrap>
                    <img
                        src={image.url}
                        alt={image.name}
                        onClick={() => setPreviewImage(image)}
                    />
                    <ImageActions>
                        <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => setPreviewImage(image)}
                        />
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => setPickerOpen(true)}
                        />
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => onImageSelect(null)}
                        />
                    </ImageActions>
                </SelectedImageWrap>
            ) : (
                <SelectButton onClick={() => setPickerOpen(true)}>
                    <PlusOutlined />
                    <span>Select image</span>
                </SelectButton>
            )}

            {/* Image Picker Modal */}
            <Modal
                open={pickerOpen}
                title="Select Image"
                footer={null}
                onCancel={closePickerModal}
                width={800}
            >
                <Input
                    placeholder="Нэрээр хайх"
                    prefix={<SearchOutlined />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    allowClear
                    style={{ marginBottom: 12 }}
                />
                {filteredImages.length === 0 ? (
                    <EmptyImages>No images found</EmptyImages>
                ) : (
                    <PickerGrid>
                        {filteredImages.map((img) => (
                            <PickerItem
                                key={img.id}
                                onClick={() => {
                                    onImageSelect(img);
                                    closePickerModal();
                                }}
                            >
                                <img src={img.url} alt={img.name || img.id} />
                                {img.name && <span>{img.name}</span>}
                            </PickerItem>
                        ))}
                    </PickerGrid>
                )}
            </Modal>

            {/* Preview Modal */}
            <Modal
                open={previewImage !== null}
                title={previewImage?.name || "Preview"}
                footer={null}
                onCancel={() => setPreviewImage(null)}
                centered
            >
                {previewImage && (
                    <img
                        src={previewImage.url}
                        alt={previewImage.name}
                        style={{ width: "100%" }}
                    />
                )}
            </Modal>

            <Divider />
            <Actions>
                <Button type="primary" onClick={handleSaveImages}>
                    Хадгалах
                </Button>
            </Actions>
        </div>
    );
};

export default ImagesTab;

const SectionTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 16px;
`;

const SelectedImageWrap = styled.div`
  img {
    width: 100%;
    max-height: 220px;
    object-fit: cover;
    border-radius: 6px;
    cursor: pointer;
    display: block;
  }
`;

const ImageActions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 8px;
  justify-content: flex-end;
`;

const SelectButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #667085;
  border: 1px dashed #d0d5dd;
  border-radius: 6px;
  padding: 40px 20px;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;

  &:hover {
    border-color: #2563eb;
    color: #2563eb;
  }
`;

const PickerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 12px;
  max-height: 460px;
  overflow-y: auto;
  padding: 4px;
`;

const PickerItem = styled.div`
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  transition: border-color 0.2s;
  text-align: center;

  img {
    width: 100%;
    height: 100px;
    object-fit: cover;
    display: block;
  }

  span {
    font-size: 11px;
    padding: 4px;
    display: block;
    color: #667085;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    border-color: #2563eb;
  }
`;

const EmptyImages = styled.div`
  text-align: center;
  color: #667085;
  padding: 32px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;
